<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div ref="containerEl" :class="$style.locationsFieldHolder" tabindex="1" @focusout="focusOut">
    <label>{{ t('searchBox.inputBox.label') }}</label>
    <div :class="$style.locationsWrapper">
      <div
        v-show="!isSearchFieldClicked"
        :class="[$style.selectedLocations, $style.searchField]"
        @click="searchFieldClicked"
      >
        <span :class="$style.searchLocations">
          {{ hasSelectedLocations ? outputString : t('searchBox.inputBox.placeholder') }}
        </span>
      </div>
      <div v-show="isSearchFieldClicked" :class="[$style.searchField, $style.locationInputActive]">
        <div
          :class="[
            $style.locationsTagHolder,
            selectedLocations.length > 0 ? $style.locationsTagHolderNotEmpty : '',
          ]"
        >
          <LocationTag
            v-for="location in selectedLocations"
            :key="location.id"
            :label="getGeoLocationLabel(location)"
            @removeClicked="onRemoveItem(location.id)"
          />
        </div>
        <input
          ref="searchField"
          :value="searchText"
          name="locations"
          :placeholder="t('searchBox.inputBox.placeholder')"
          :class="$style.searchFieldInput"
          type="text"
          @input="inputChange"
          @keyup.enter="keyEnter"
          @keydown.up.prevent="keyUp"
          @keydown.down.prevent="keyDown"
          @keydown.backspace="keyBackspace"
        />

        <div
          v-if="searchText.length > 1 && geoLocationsWithoutAlreadySelectedOnes.length"
          :class="$style.itemsHolder"
        >
          <p
            v-for="(geoSearchResultItem, i) in geoLocationsWithoutAlreadySelectedOnes"
            :key="geoSearchResultItem.geoLocation.id"
            :class="[$style.slicedItem, { [$style.activeItem]: i === cursor }]"
            @mouseover="cursor = i"
            @click="onSelectGeoLocation(geoSearchResultItem.geoLocation)"
          >
            <span v-html="getHighlightedMatch(geoSearchResultItem)" />
            <span>{{ locationTypeLabel(geoSearchResultItem.geoLocation) }}</span>
          </p>
        </div>
      </div>
    </div>

    <p v-if="isNoLocationSelectedErrorVisible" :class="$style.error">
      {{ t('searchBox.noLocationSelectedWarning') }}
    </p>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable vue/multi-word-component-names */
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, GeoSearchResultItem } from '@smg-real-estate/geo-libs';
import throttle from 'lodash.throttle';
import { computed, nextTick, ref, watch } from 'vue';

import { getDistinguishedDisplayValue } from '@/helpers/Geo/GeoLocationHelper';
import { getHighlightedDisplayValue } from '@/helpers/Geo/GeoSearchResultHelper';

import { ValidationStates } from '@/components/Locations/validationStates';
import LocationTag from './LocationTag.vue';

interface Props {
  /** Geo search results. */
  searchResults: GeoSearchResultItem[];
  /** Already selected locations. */
  selectedLocations: GeoLocation[];
  /** Current language. */
  locale: HgSupportedLanguage;
  /** i18n translate function injected from parent */
  t: (key: string, params?: Record<string, any>) => string;
  validationState: ValidationStates;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  searchTextUpdated: [value: string];
  locationsChanged: [locations: GeoLocation[]];
}>();

// Template refs
const containerEl = ref<HTMLElement | null>(null);
const searchField = ref<HTMLInputElement | null>(null);

// Local reactive state
const searchText = ref<string>('');
const internalSearchResultItems = ref<GeoSearchResultItem[]>(props.searchResults || []);
const cursor = ref<number>(-1);
const isSearchFieldClicked = ref<boolean>(false);

// Computed
const selectedGeoIds = computed<string[]>(() => props.selectedLocations.map(({ id }) => id));

const geoLocationsWithoutAlreadySelectedOnes = computed<GeoSearchResultItem[]>(() =>
  internalSearchResultItems.value.filter(
    ({ geoLocation }) => selectedGeoIds.value.indexOf(geoLocation.id) === -1,
  ),
);

const hasSelectedLocations = computed<boolean>(() => props.selectedLocations.length > 0);

const outputString = computed<string>(() => {
  let result = '';
  if (props.selectedLocations.length > 0) {
    result = getDistinguishedDisplayValue(props.selectedLocations[0], props.locale);
  }
  if (props.selectedLocations.length > 1) {
    result += ' +' + (props.selectedLocations.length - 1);
  }
  return result;
});

const isNoLocationSelectedErrorVisible = computed<boolean>(
  () => props.validationState === ValidationStates.InvalidShowError,
);

// Watchers
watch(
  () => props.searchResults,
  (newSearchResults) => {
    setItems(newSearchResults);
  },
);

// Throttled emitter (mirror of created() behavior)
const emitSearchText = throttle(() => {
  const value = searchText.value;
  if (value.length >= 2) emit('searchTextUpdated', value);
}, 300);

// Methods
const t = props.t;

function focusInput(): void {
  searchField.value?.focus();
}

function searchFieldClicked(): void {
  isSearchFieldClicked.value = !isSearchFieldClicked.value;
  if (isSearchFieldClicked.value) {
    nextTick(() => focusInput());
  }
}

function inputChange(event: any): void {
  cursor.value = -1;
  if (event?.target) {
    searchText.value = event.target.value || '';
    emitSearchText();
  }
}

function setItems(items: GeoSearchResultItem[]): void {
  internalSearchResultItems.value = items || [];
}

function focusOut(event: FocusEvent): void {
  const related = event.relatedTarget as Node | null;
  const root = containerEl.value;
  isSearchFieldClicked.value = !!(root && related && root.contains(related));
}

function getHighlightedMatch(geoSearchResultItem: GeoSearchResultItem): string {
  return getHighlightedDisplayValue(geoSearchResultItem, props.locale);
}

function keyEnter(): void {
  if (geoLocationsWithoutAlreadySelectedOnes.value[cursor.value]) {
    onSelectGeoLocation(geoLocationsWithoutAlreadySelectedOnes.value[cursor.value].geoLocation);
  }
}

function keyUp(): void {
  if (cursor.value > -1) cursor.value--;
}

function keyDown(): void {
  if (cursor.value < geoLocationsWithoutAlreadySelectedOnes.value.length - 1) cursor.value++;
}

function keyBackspace(): void {
  if (!hasSelectedLocations.value || !!searchText.value) return;

  const lastLocationId = props.selectedLocations[props.selectedLocations.length - 1].id;
  emit(
    'locationsChanged',
    props.selectedLocations.filter((location) => location.id !== lastLocationId),
  );
}

function onSelectGeoLocation(geoLocation: GeoLocation | undefined): void {
  if (geoLocation) {
    internalSearchResultItems.value = [];
    searchText.value = '';
    emit('locationsChanged', [...(props.selectedLocations || []), geoLocation]);
  } else {
    setItems(props.searchResults);
  }
  focusInput();
}

function onRemoveItem(geoId: string): void {
  emit(
    'locationsChanged',
    props.selectedLocations.filter((location) => location.id !== geoId),
  );
  focusInput();
}

function getGeoLocationLabel(geoLocation: GeoLocation): string {
  return getDistinguishedDisplayValue(geoLocation, props.locale, { inclAllNames: true });
}

function locationTypeLabel(geoLocation: GeoLocation): string | undefined {
  return geoLocation.typeLabel?.[props.locale];
}
</script>

<style lang="scss" module>
@use '../../helpers/styles/searchbarFields' as mixins;
@use '@/assets/variables' as *;

.locationsWrapper {
  position: relative;
  margin-top: 10px;

  @media (min-width: $viewport-sm-min) {
    height: 44px;
  }
}

.locationsFieldHolder {
  width: 100%;

  label {
    @include label;
  }
}

.searchField {
  position: relative;

  @include mixins.search-field;

  &.selectedLocations {
    display: flex;
    align-items: center;
    height: 44px;
  }

  .searchLocations {
    display: -webkit-box;
    padding: 0 $space-150;
    margin-left: $space-100;
    overflow: hidden;
    line-height: 1.5rem;
    -webkit-line-clamp: 1;
    word-break: break-all;
    background: url('./search.png?assetInline') no-repeat 0 4px;
    -webkit-box-orient: vertical;
  }

  .searchFieldInput {
    padding-left: $space-250;
  }
}

.locationInputActive {
  z-index: 4;
  display: block;

  input {
    height: 2.6rem;
    padding: $space-75 $space-250;
    background: url('./search.png?assetInline') no-repeat 16px 13px;
    border: none !important;
    outline: none;

    @include input;
  }
}

.locationsTagHolder {
  display: flex;
  flex-wrap: wrap;
  gap: $space-100;

  &.locationsTagHolderNotEmpty {
    padding: $space-100;
  }
}

.itemsHolder {
  position: absolute;
  width: 100%;
  min-height: 15rem;
  max-height: calc(100vh - 23rem);
  overflow-y: auto;
  cursor: pointer;
  background-color: $color-white;
  border: 1px solid $color-grey-50;
  border-top: none;
  border-radius: 0 0 $border-radius $border-radius;

  @media (min-width: $viewport-sm-min) {
    max-height: calc(100vh - 535px);
  }

  @media (min-width: $viewport-lg-min) {
    max-height: calc(100vh - 677px);
  }
}

.slicedItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-50 $space-100;
  margin: 0;

  &.activeItem {
    background-color: $color-blue-0;
  }

  span {
    display: block;
    color: $color-grey-90;

    &:last-child {
      color: $color-grey-70;

      @include body-3;
    }
  }
}

.error {
  position: absolute;
  margin-top: calc($space-25 / 2 * -1);
  color: $color-orange-90;

  @include caption;
}
</style>
